// frontend/src/pages/LinkInBio.js
//
// Public landing page for the link-in-bio feature.
//
// RETARGETED 2026-08-20. The page shipped 2026-08-12 aimed at `link in bio`
// generally, which was never a specific target — 12,100/mo at KD 32, owned by
// Linktree (16,865 referring domains) and Canva. It now targets
// `link in bio tool for instagram`: 1,600/mo, KD 24, and crucially WALL 16.
//
// The wall is what changed the decision. lnk.bio's 1,626 referring domains make
// that SERP look shut, but it is a homepage and the MEDIAN is what matters:
// shinewithnatasha ranks with 2 referring domains, sitebuilderreport with 6,
// Canva 30, Sprout Social 26. Median of the top four = 16, comparable to the
// LinkedIn formatter's 14, which we judged winnable.
//
// Format was checked before committing (standing rule 6). Three of the six
// results are PRODUCT pages — lnk.bio, Canva and Sprout Social, whose page is
// literally titled "Instagram Link in Bio Tool". A product page can rank here.
//
// The SERP overlaps heavily with `link in bio tool` (6,600/mo, KD 27) — lnk.bio,
// Canva, shinewithnatasha and sitebuilderreport appear on both — so this page is
// positioned for the bigger term without targeting it head-on.
//
// VOLUME HONESTY: `link in bio tool`, `link in bio tools` and `link-in-bio
// tools` are all reported at exactly 6,600/mo at KD 27, and both Instagram
// variants at exactly 1,600/KD 24. Those are Keyword Planner close-variant
// buckets counted once, not summed. Distinct volume here is ~1,600.
//
// NOT TARGETED, and recorded so the reasoning survives: `linktree alternatives`
// is 2,400/mo at KD 1 — extraordinary numbers — but the SERP is wpforms,
// ecomm.design and mobilocard LISTICLES plus an AI Overview. A product page
// cannot win a listicle SERP. It is a genuine opportunity for a separate
// comparison page, which is a different asset from this one.
//
// Every claim here is checked against the implementation:
// Every claim here is checked against the implementation:
//   backend/routes/bio.js  — FREE_LINK_LIMIT = 5, click tracking, subscribe
//   pages/bio/bioThemes.js — light, midnight, ocean, sunset, forest, bold
//   pages/bio/PublicBioPage.js — renders EmailCapture + LatestPosts
// If the feature changes, change this page too.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Card, Accordion } from 'react-bootstrap'
import { Link45deg, BarChart, Envelope, Palette, Clock, CheckCircleFill } from 'react-bootstrap-icons'

const THEMES = ['Light', 'Midnight', 'Ocean', 'Sunset', 'Forest', 'Bold']

const FEATURES = [
  { icon: <Link45deg />,    title: 'Unlimited links',      body: 'Add as many links as you need and drag them into any order. Free accounts get five.' },
  { icon: <BarChart />, title: 'Click analytics',      body: 'See which links people actually tap, so you can put the ones that convert at the top.' },
  { icon: <Envelope />, title: 'Email capture',        body: 'Collect email addresses straight from your bio page and build a list you own.' },
  { icon: <Clock />,    title: 'Your latest posts',    body: 'Show your most recent social posts automatically, so the page stays current without you touching it.' },
  { icon: <Palette />,  title: 'Six themes',           body: 'Light, Midnight, Ocean, Sunset, Forest and Bold — with your own colours and avatar.' },
  { icon: <CheckCircleFill />, title: 'Built into your scheduler', body: 'The same account that schedules your posts hosts your bio page. One login, one bill.' },
]

// The last four target measured long-tail queries rather than being invented:
// `what does link in bio mean` 880/mo KD 0, `how to put a link in instagram bio`
// 390/mo, `how to add link in bio instagram` 320/mo, `how to add link in tiktok
// bio` 720/mo KD 0. All definitional or how-to, so they belong in the FAQ where
// they can earn a snippet — not in the body, which stays a product page.
const FAQS = [
  ['What does "link in bio" mean?',
   'It is the phrase people use because Instagram does not allow clickable links in captions or comments — only one, in your profile. "Link in bio" is shorthand for "the thing I am talking about is behind the single link on my profile". A link in bio tool turns that one slot into a page holding as many destinations as you need.'],
  ['How do I put a link in my Instagram bio?',
   'Open your profile, tap Edit profile, and paste the link into the Website field. If you want more than one destination behind it, paste your bio page link there instead — then everything you add to that page is reachable without editing your profile again.'],
  ['How do I add a link in my TikTok bio?',
   'TikTok shows a website field on your profile once your account qualifies for it; paste your bio page link there. Until then, put the link in your profile text — a short, memorable URL matters more on TikTok because people often have to type it rather than tap it.'],
  ['Is the link in bio page free?',
   'Yes. Free accounts get a bio page with up to five links. Paid plans, from $9/month, remove the limit.'],
  ['What will my link look like?',
   'greatlysocial.com/u/yourname — you choose the username, and we check availability as you type.'],
  ['How is this different from Linktree?',
   'The page itself is comparable — links, themes, click analytics. Two differences matter. It comes with the tool that schedules your posts, so the page shows your latest content automatically instead of going stale the moment you stop editing it. And it is one subscription rather than two, because you were going to pay for a scheduler anyway.'],
  ['Can I collect email addresses?',
   'Yes. Add an email capture block to your page and subscribers are saved to your account, ready to use in email campaigns.'],
  ['Can I see how many people clicked?',
   'Yes. Every link tracks its own clicks, and the analytics tab shows you which ones are working.'],
  ['Which platforms can I use it on?',
   'Anywhere that allows one link — Instagram, TikTok, Threads, X, YouTube, Pinterest, LinkedIn or a Facebook profile.'],
  ['Do I need a website?',
   'No. The page is hosted for you and works on its own.'],
]

export default function LinkInBio () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={7}>
              <h1 className='display-5 fw-bold mb-3'>Instagram link in bio tool</h1>
              <p className='lead mb-4'>
                One link for everything you share on Instagram, TikTok and the rest. Add your
                links, collect emails, and show your latest posts automatically — from the same
                account that schedules them.
              </p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Create your free page
              </Button>
              <Button variant='outline-primary' size='lg' href='/plans' className='mb-2'>
                See plans
              </Button>
              <p className='text-muted small mt-3 mb-0'>
                Free to start · your own <code>greatlysocial.com/u/yourname</code> link
              </p>
            </Col>
            <Col md={5}>
              {/* Simple phone mock rather than a stock photo — it shows the actual
                  thing being sold, which a photo of a person holding a phone does not. */}
              <div className='mx-auto' style={{
                maxWidth: 260, borderRadius: 28, border: '10px solid #111827',
                background: 'linear-gradient(160deg,#1e3a5f,#2563eb)', padding: '28px 18px',
                boxShadow: '0 18px 50px rgba(15,23,42,.28)',
              }}>
                <div style={{
                  width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,.9)',
                  margin: '0 auto 12px',
                }} />
                <div className='text-center text-white fw-bold mb-1'>@yourname</div>
                <div className='text-center mb-3' style={{ color: 'rgba(255,255,255,.7)', fontSize: 12 }}>
                  Everything I&rsquo;m sharing this week
                </div>
                {['Latest video', 'Shop the collection', 'Free guide', 'Book a call'].map(t => (
                  <div key={t} style={{
                    background: 'rgba(255,255,255,.95)', borderRadius: 10, padding: '9px 12px',
                    marginBottom: 8, fontSize: 12, fontWeight: 700, color: '#1e3a5f', textAlign: 'center',
                  }}>{t}</div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── DEFINITION (snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            A <strong>link in bio tool</strong> solves the problem Instagram creates: your
            profile gives you <strong>one</strong> clickable link, and you almost always have
            more than one thing worth sending people to. Instead of rewriting that link every
            time something changes, you point it at a single page holding all of them — and
            edit the page instead.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Your link stays <code>greatlysocial.com/u/yourname</code>. What sits behind it is
            yours to change whenever you like. It works the same way on TikTok, Threads, X,
            YouTube, Pinterest and LinkedIn — anywhere you get one link and need several.
          </p>
        </Container>
      </section>

      {/* ───────── FEATURES ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-3'>What you get</h2>
            </Col>
          </Row>
          <Row className='g-4'>
            {FEATURES.map(({ icon, title, body }) => (
              <Col md={4} key={title}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='fs-3 text-primary mb-2'>{icon}</div>
                    <Card.Title className='fw-semibold h6'>{title}</Card.Title>
                    <Card.Text className='text-muted small'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── THE DIFFERENCE ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 820 }}>
          <h2 className='fw-bold mb-3'>Why keep it with your scheduler?</h2>
          <p className='text-muted fs-5'>
            Most link-in-bio pages are a separate product with a separate subscription, and
            they go stale because updating them is one more chore. Ours sits inside the tool
            you already use to post.
          </p>
          <Row className='g-4 mt-1'>
            {[
              ['It updates itself', 'Your bio page can show your latest posts automatically. Publish as normal and the page stays current.'],
              ['One subscription', 'It is included, not billed separately. Free to start, unlimited links from $9/month.'],
              ['Your list, not a platform’s', 'Emails captured on your page land in your account, ready for campaigns — not locked in a tool you have to export from.'],
            ].map(([t, b]) => (
              <Col md={4} key={t}>
                <p className='d-flex align-items-start gap-2 mb-0'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <span><strong>{t}</strong><br /><span className='text-muted small'>{b}</span></span>
                </p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── THEMES ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 820 }}>
          <h2 className='fw-bold mb-3'>Six themes, or make your own</h2>
          <p className='text-muted fs-5 mb-4'>
            Start from a theme and adjust the background, buttons, text and avatar until it
            looks like you.
          </p>
          <div className='d-flex flex-wrap gap-2'>
            {THEMES.map(t => (
              <span key={t} className='badge rounded-pill bg-white text-dark border px-3 py-2 fw-semibold'>
                {t}
              </span>
            ))}
          </div>
        </Container>
      </section>

      {/* ───────── STEPS ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4'>Set it up in three steps</h2>
          <ol className='fs-5 text-muted ps-3'>
            <li className='mb-3'><strong>Claim your username.</strong> Pick the name for your <code>/u/</code> link — we check availability as you type.</li>
            <li className='mb-3'><strong>Add your links.</strong> Paste them in, name them, drag them into the order you want.</li>
            <li><strong>Put it in your bio.</strong> Copy your link into Instagram, TikTok, Threads or anywhere else that allows one.</li>
          </ol>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Link in bio FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>Claim your Greatly Social link</h2>
          <p className='lead mb-4'>At Greatly Social your bio page updates itself from the posts you schedule. Free to start, unlimited links from $9/month.</p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Create your page
          </Button>
          <Button variant='outline-light' size='lg' href='/post-scheduling' className='mb-2'>
            See scheduling
          </Button>
        </div>
      </section>
    </>
  )
}
